import { ApiValidation, ApiValidationErrorType } from "../../model";
import { DataRow } from "./DataRow";
import { ImportResult } from "./ImportResult";
import { Importable } from "./Importable";
import { FileParser } from "./FileParser";
import { Culture } from "./Culture";
import { progressNotification } from "../../ui";
import { DataMapper } from "./DataMapper";
import { ValidationHelper } from "../ValidationHelper";
import { JsonSchemaUtils } from "../../util";
import { Translations } from "../Translations";

/**
 * Logic for the batch import of data from sequence files.
 * @param TTarget is the target item data type of the import.
 */
export class ImportBatch<TTarget> {

  /** Raw file data for importing. E.g. csv or tab formatted file. */
  public rawData: string;

  /** Parsed tabular data but not mapped */
  public unmappedData: ImportResult<DataRow>;

  /** The data mapper used by the batch. */
  public mapper: DataMapper<any, any>;

  /**
   * A pre-mapper for the parsing logic:
   * Used by the user interface to map the parsed text file data to assumed / originally defined source.
   */
  public parserMapper: DataMapper<any, any>;

  /**
   * Creates a new batch for data import.
   * @param mapper Mapper object for table to objects transformations.
   * Preselected from the Importable API.
   * @param api Api object for validting and posting data to API.
   */
  constructor(mapper: DataMapper<any, any>, private api: Importable<TTarget>) {
    this.setMapper(mapper);
  }

  /** Result data of the current operation (map, validate, import). */
  public get data(): TTarget[] {
    return this.mapper?.target?.value;
  }

  /**
   * Sets the mapper for the batch.
   * @param mapper The new data mapper.
   */
  public setMapper(mapper: DataMapper<any, any>): void {
    this.mapper = mapper;
    this.resetParserMapper();
  }

  /** Resets the parser mapper according unmappedData (source) and mapper source (target) */
  public resetParserMapper(): void {
    const mainMapperSourceSchema = this.mapper?.source?.getSchema();
    if (!mainMapperSourceSchema) {
      this.parserMapper = null;
      return;
    }
    const mapper = this.parserMapper = new DataMapper("temp/parserMapper", "Parser mapper: From text file to defined source.");
    mapper.setSchema(mainMapperSourceSchema, this.mapper?.source.name, "target");
    const parserSourceSchema = JsonSchemaUtils.guessSchema(this.unmappedData?.data[0]);
    if (!parserSourceSchema) {
      return;
    }
    mapper.setSchema(parserSourceSchema, "inputFileSource", "source");
    const map = {};
    const targetKeys = mapper.target.keys;
    mapper.source.keys.forEach((key) => {
      map[key] = (targetKeys.indexOf(key) >= 0) ? key : null;
    });
    mapper.setPaths(map);
    mapper.source.value = this.unmappedData?.data;
  }

  /**
   * Read data into batch from the import file content (to unmappedData).
   * @param rawData File content, e.g. csv or tab separated data.
   * @param parser Parser for parsing data to tabular format.
   * @param localeId If 'fi', the number and date formats are parsed using Finnish formats.
   * @returns the status of the read.
   */
  public read(rawData: string = null, parser: FileParser, localeId: Culture = Culture.Fi): ApiValidation {
    this.rawData = rawData || this.rawData;
    this.unmappedData = parser.parse(this.rawData, localeId);
    this.resetParserMapper();
    const status = new ValidationHelper({
      errors: this.unmappedData.errors,
    });
    if (!this.unmappedData.data.length) {
      status.warn("WARNING: Import contains no data!", "import.warning.noData");
    }
    if (this.unmappedData.ignoredLines) {
      status.warn(`${this.unmappedData.ignoredLines} ${Translations.get("SALAXY.NG1.ODataImportComponent.step2.emptyRow")}`, "import.ignoredLines.warning");
    }
    status.recalc();
    return status.validation;
  }

  /**
   * Maps the source format to the target format using the mapper.
   */
  public map(): void {
    if (!this.mapper) {
      return;
    }
    if (this.parserMapper) {
      this.parserMapper.source.value = this.unmappedData.data;
      this.parserMapper.doMap();
      this.mapper.source.value = this.parserMapper.target.value;
    } else {
      this.mapper.source.value = this.unmappedData.data;
    }
    this.mapper.doMap();
  }

  /** Returns true if the import data is valid */
  public get isDataValid(): boolean {
    return this.data?.length && this.data.every((x) => this.getMeta(x).validation?.isValid);
  }

   /** Returns true if the import data contains validation errors */
   public get hasDataValidationErrors(): boolean {
    return this.data?.length && this.data.some((x) => this.getMeta(x).validation && this.getMeta(x).validation.errors && !!this.getMeta(x).validation.errors.length);
  }

  /** Clears validation from the rows */
  public clearValidation(): void {
    for (const row of this.mapper.source.value) {
      this.getMeta(row).validation = null;
      this.getMeta(row).object = null;
    }
    for (const row of this.data) {
      this.getMeta(row).validation = null;
      this.getMeta(row).object = null;
    }
  }

  private getMeta(obj: any): { validation: ApiValidation, object: any } {
    return obj || {};
  }

  /**
   * Validates the batch data.
   * @param notify Function for notifying progress to caller.
   * @returns A validation object with potential errors.
   */
  public validate(notify: progressNotification<ApiValidation> = null): Promise<ApiValidation> {

    const tryValidate = (obj: TTarget, ix: number, all: TTarget[]): Promise<ApiValidation> => {
      try {
        return this.api.validate(obj, ix, all).then((apiResult) => {
          return apiResult?.validation;
        });
      } catch (error) {
        return Promise.resolve({
          isValid: false,
          errors: [{
            key: null,
            type: ApiValidationErrorType.General,
            code: error.name || "Server error",
            msg: error.message || JSON.stringify(error, Object.getOwnPropertyNames(error)),
          }],
        });
      }
    };

    const status = new ValidationHelper({});

    this.clearValidation();
    const objects = this.data;
    let index = 0;
    const next = (): Promise<void> => {
      if (index === objects.length) {
        return Promise.resolve();
      }
      const obj = objects[index];
      return tryValidate(obj, index, objects)
        .then((validationResult) => {
          if (notify) {
            notify("validate", index, objects.length, validationResult);
          }
          this.updateValidation(index, validationResult);
          status.merge(validationResult);
          index++;
          return next();
        });
    };

    return next().then(() => {
      status.recalc();
      return status.validation;
    });
  }

  /**
   * Imports the batch data.
   * @param notify Function for notifying progress to caller.
   */
  public import(notify: progressNotification<ApiValidation> = null): Promise<ApiValidation> {

    const tryImport = (obj: TTarget): Promise<
      {
        /** saved object */
        object: TTarget,
        /** error */
        validation: ApiValidation,
      }> => {
      try {
        return this.api.importItem(obj).then((apiResult) => {
          return { object: apiResult, validation: { isValid: true } };
        });
      } catch (error) {
        return Promise.resolve({
          object: obj,
          validation: {
            isValid: false,
            errors: [{
              key: null,
              type: ApiValidationErrorType.General,
              code: error.name || "Server error",
              msg: error.message || JSON.stringify(error, Object.getOwnPropertyNames(error)),
            }],
          },
        });
      }
    };

    this.clearValidation();
    const status = new ValidationHelper({});

    const objects = this.data;
    let index = 0;
    const next = (): Promise<void> => {
      if (index === objects.length || !this.api.importItem) {
        return Promise.resolve();
      }
      const obj = objects[index];
      return tryImport(obj)
        .then((tryResult) => {
          if (notify) {
            notify("import", index, objects.length, tryResult.validation);
          }
          this.updateValidation(index, tryResult.validation);
          status.merge(tryResult.validation);
          index++;
          return next();
        });
    };
    status.recalc();
    return next().then(() => {
      if (this.api.finalize) {
        this.api.finalize(objects).then(() => {
          return status.validation;
        });
      }
      return status.validation;
    });
  }

  private updateValidation(objectIndex: number, validation: ApiValidation): void {
    // TODO: Consider is it possible to show validation when 1-to-n mapping
    // TODO: Consider should we have this as $meta.validation
    this.getMeta(this.mapper.source.value[objectIndex]).validation = validation;
    this.getMeta(this.data[objectIndex]).validation = validation;
  }
}
